import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { ROUTE_GROUPS } from 'routes/config';
import { normalizePerformanceRoute, PerformanceTracker, startPerformanceTracking } from 'services/performanceMetrics';
import { getAuthGeneration } from 'utils/auth';

const routeTemplates = ROUTE_GROUPS.flatMap((group) => group.routes.map((entry) => entry.path));

export const PerformanceTracking = () => {
  const { user, isAuthenticated, isAuthLoading } = useAuth();
  const location = useLocation();
  const identity = !isAuthLoading && isAuthenticated ? user?.id ?? null : null;
  const authGeneration = getAuthGeneration();
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const tracker = useRef<PerformanceTracker | null>(null);
  const initialPath = useRef(location.pathname);
  const initialDocumentEligible = useRef(true);
  const initialIdentity = useRef<number | null>(null);
  const initialGeneration = useRef<number | null>(null);
  const route = normalizePerformanceRoute(location.pathname, routeTemplates);
  const routeRef = useRef(route);
  routeRef.current = route;
  const previousLocation = useRef(location.key);
  const locationRef = useRef(location);
  locationRef.current = location;

  useLayoutEffect(() => {
    if (!isAuthLoading && identity === null) initialDocumentEligible.current = false;
    if (identity === null) return;
    if (initialIdentity.current === null) initialIdentity.current = identity;
    if (initialGeneration.current === null) initialGeneration.current = authGeneration;
    if (initialIdentity.current !== identity || initialGeneration.current !== authGeneration) initialDocumentEligible.current = false;
    const activeIdentity = identity;
    tracker.current = startPerformanceTracking(
      routeRef.current,
      initialDocumentEligible.current && initialPath.current === locationRef.current.pathname,
      () => identityRef.current === activeIdentity,
    );
    previousLocation.current = locationRef.current.key;
    return () => { tracker.current?.stop(); tracker.current = null; };
  }, [identity, isAuthLoading, authGeneration]);

  useLayoutEffect(() => {
    if (previousLocation.current !== location.key) {
      previousLocation.current = location.key;
      tracker.current?.routeChanged(route);
    }
  }, [location.key, route]);
  return null;
};
